import { motion } from "framer-motion";
import { ASSETS } from "@/lib/brand";

export default function Collection() {
  return (
    <section
      id="collezione"
      data-testid="collection-section"
      className="relative bg-[#0A0A0A] text-[#F2EFE8] overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-25" aria-hidden />

      <div className="relative max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24 lg:py-28">
        {/* Header row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-6 mb-6">
              <span
                className="section-num text-3xl"
                style={{ color: "#C4A671" }}
              >
                02
              </span>
              <div className="h-px w-12" style={{ background: "#C4A671" }} />
              <span
                className="overline text-[10px]"
                style={{ color: "#C4A671" }}
              >
                Collezione — Vintage 2025
              </span>
            </div>

            <h2
              className="font-serif-display leading-[0.95] text-[#F2EFE8]"
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.8rem)" }}
            >
              Quattro gesti,
              <br />
              <span className="italic font-light" style={{ color: "#C4A671" }}>
                una firma.
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p
              className="text-[15.5px] leading-[1.9] font-light"
              style={{ color: "#C9C3B5" }}
            >
              Quattro gesti, una firma. La collezione firmata{" "}
              <em className="font-serif-display not-italic text-[#F2EFE8]">
                Claudio Pucci
              </em>
              : la Linea Territorio ({" "}
              <em className="font-serif-display not-italic text-[#F2EFE8]">
                Scilla
              </em>{" "}
              e{" "}
              <em className="font-serif-display not-italic text-[#F2EFE8]">
                Terra Arsa
              </em>{" "}
              ) e la Linea Limited Edition:{" "}
              <em className="font-serif-display not-italic" style={{ color: "#C4A671" }}>
                Blanche
              </em>
              , numerata a mano in sole 1.333 bottiglie trasparenti, e{" "}
              <em className="font-serif-display not-italic" style={{ color: "#C4A671" }}>
                Claire
              </em>
              , il rosato d&apos;autore in micro-lotto. Ogni bottiglia porta lo
              stesso crest araldico, la stessa ossessione per il dettaglio.
            </p>
          </div>
        </motion.div>

        {/* Full-bleed image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="relative"
        >
          <div className="relative border max-w-[980px] mx-auto" style={{ borderColor: "#1a1a1a" }}>
            <img
              src={ASSETS.collection}
              alt="Collezione 2025 — Scilla, Terra Arsa, Blanche e Claire: Linea Territorio e Linea Limited Edition"
              className="w-full h-auto"
              data-testid="collection-image"
            />
          </div>

          {/* Four labels overlay */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <LineCard
              chapter="Il Bianco"
              name="Scilla"
              denom="Inzolia · Terre Siciliane I.G.T."
              vol="12,5% VOL · 750 ML"
              testid="collection-card-scilla"
            />
            <LineCard
              chapter="Il Rosso"
              name="Terra Arsa"
              denom="Nero d'Avola · Sicilia D.O.C."
              vol="13% VOL · 750 ML"
              testid="collection-card-terra-arsa"
            />
            <LineCard
              chapter="Limited Edition"
              name="Blanche"
              denom="Blanc de Noirs & Blend d'Autore"
              vol="1.333 Bottiglie Trasparenti · Vintage 2025"
              highlight
              testid="collection-card-blanche"
            />
            <LineCard
              chapter="Il Rosato · Limited Edition"
              name="Claire"
              denom="Rosato di Frappato & Nero d'Avola"
              vol="Micro-lotto · Vintage 2025"
              highlight
              testid="collection-card-claire"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function LineCard({ chapter, name, denom, vol, highlight, testid }) {
  const goldBorder = "#C4A671";
  return (
    <div
      className="px-5 py-5 border transition-colors"
      style={{
        background: highlight ? "rgba(196,166,113,0.06)" : "rgba(255,255,255,0.02)",
        borderColor: highlight ? goldBorder : "#1a1a1a",
      }}
      data-testid={testid}
    >
      <p
        className="overline text-[9px] mb-3"
        style={{ color: highlight ? goldBorder : "#7a7a7a" }}
      >
        {chapter}
      </p>
      <p
        className="font-serif-display italic text-2xl leading-tight"
        style={{ color: highlight ? goldBorder : "#F2EFE8" }}
      >
        {name}
      </p>
      <p className="mt-3 text-[13.5px] leading-[1.6] font-light text-[#C9C3B5]">
        {denom}
      </p>
      <p
        className="mt-1 overline text-[9px]"
        style={{ color: "#7a7a7a", letterSpacing: "0.2em" }}
      >
        {vol}
      </p>
    </div>
  );
}
